import { Helmet } from 'react-helmet-async';
import { useState, useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import { Box, Stack, Button, Popover, MenuItem, IconButton, Tooltip } from '@mui/material';
import { DataGrid, Toolbar } from '@mui/x-data-grid';

import Iconify from '../../components/iconify';
import { ToolBarSearch, ToolBarColumnToggler, Container as DataGridContainer } from '../../components/datagrid';
import { useGetSectionsQuery, useDeleteSectionMutation } from '../../slices/apis/sectionApi';
import type { SectionProps } from '../../slices/apis/apis.types';

type DataGridRow = {
	id: SectionProps['id'];
	name: SectionProps['name'];
	description: SectionProps['description'];
	actions: string;
};

export default function Sections() {
	const [menuOpen, setMenuOpen] = useState<HTMLButtonElement | null>(null);
	const [selectedId, setSelectedId] = useState<number | null>(null);

	const navigate = useNavigate();
	const theme = useTheme();
	const { data = [], isFetching } = useGetSectionsQuery();
	const [deleteSection] = useDeleteSectionMutation();

	const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
		setMenuOpen(event.currentTarget);
		setSelectedId(Number(event.currentTarget.getAttribute('data-section-id')));
	};

	const handleCloseMenu = () => {
		setMenuOpen(null);
	};

	const handleDelete = () => {
		if (selectedId != null) deleteSection(selectedId);
		setMenuOpen(null);
	};

	const handleEdit = () => {
		navigate(`/dashboard/admin/sections/${selectedId}/edit`);
		setMenuOpen(null);
	};

	const handleNavigate = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			navigate(event.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	return (
		<>
			<Helmet>
				<title>Sections - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Box
				sx={{
					width: { xs: '100%', md: '85%' },
					ml: { xs: 0, md: 3, lg: 4 },
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Button
						variant="contained"
						data-route="/dashboard/admin/sections/new"
						onClick={handleNavigate}
						startIcon={<Iconify icon="eva:plus-fill" />}
					>
						New Section
					</Button>
				</Stack>

				<DataGridContainer>
					<DataGrid
						disableColumnFilter
						disableDensitySelector
						disableColumnSelector
						showToolbar
						loading={isFetching}
						columnHeaderHeight={48}
						slots={{
							toolbar: () => (
								<Toolbar>
									<Box sx={{ pl: 1 }}>
										<ToolBarSearch />
									</Box>
									<Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
										<Tooltip title="Columns">
											<ToolBarColumnToggler />
										</Tooltip>
									</Box>
								</Toolbar>
							),
						}}
						sx={{
							width: '100%',
							borderRadius: 3,
							'& .MuiDataGrid-columnHeaderTitle': {
								whiteSpace: 'normal',
								lineHeight: 1.4,
								fontSize: '0.9rem',
								color: 'grey',
							},
							'& .MuiDataGrid-columnHeader': {
								backgroundColor: theme.palette.grey[100],
							},
						}}
						columns={[
							{
								field: 'id',
								headerName: 'ID',
								resizable: false,
								sortable: false,
								flex: 0.25,
							},
							{
								field: 'name',
								headerName: 'Name',
								resizable: false,
								flex: 1,
							},
							{
								field: 'description',
								headerName: 'Description',
								resizable: false,
								flex: 1.5,
								renderCell: (params) => params.row.description ?? '—',
							},
							{
								field: 'actions',
								headerName: '',
								headerAlign: 'center',
								align: 'right',
								flex: 0.3,
								sortable: false,
								filterable: false,
								resizable: false,
								renderCell: (params) => {
									const {
										row: { id },
									} = params;

									return (
										<IconButton size="large" color="inherit" data-section-id={id} onClick={handleOpenMenu}>
											<Iconify icon="eva:more-vertical-fill" />
										</IconButton>
									);
								},
							},
						]}
						rows={data.map((section) => {
							return {
								id: section.id,
								name: section.name,
								description: section.description,
								actions: '',
							} as DataGridRow;
						})}
					/>
				</DataGridContainer>
			</Box>

			<Popover
				open={Boolean(menuOpen)}
				anchorEl={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 1,
						width: 160,
						'& .MuiMenuItem-root': {
							px: 1,
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<MenuItem onClick={handleEdit}>
					<Iconify icon="eva:edit-fill" sx={{ mr: 1 }} />
					View / Edit
				</MenuItem>
				<MenuItem sx={{ color: 'error.main' }} onClick={handleDelete}>
					<Iconify icon="eva:trash-2-outline" sx={{ mr: 1 }} />
					Delete
				</MenuItem>
			</Popover>
		</>
	);
}
